// SearchPage — the dedicated `/search` results page (RFC-0011 Task 3.1;
// PRD-0001 §1.4; PD-0017 P5). Replaces the RFC-0009 placeholder with a live,
// URL-driven search over `GET /api/search` (RFC-0007), sharing the Phase-1 data
// layer (useSearch), result row (SearchResultItem), and scope control
// (RepoScopeToggle) with the Cmd-K palette.
//
// URL is the single source of truth (mirrors repositories.tsx): `q`, `repo`
// (bare short name; absent = all projects), the `type`/`status`/`owner` facet
// filters, and `page` all live in `?…` — so a filtered/scoped view is shareable,
// bookmarkable, and back/forward navigable. Editing the query, toggling a facet
// chip, changing scope, or paging writes back to the URL, which re-drives
// useSearch. `per_page=20` is sent EXPLICITLY (the API default is 10; 20 is a
// valid client choice ≤50).
//
// Facet chips are built ONLY from the facet value→count maps the API actually
// serves (`facets.docType/docStatus/docOwner`) — never invented. Match
// highlighting is delegated to SearchResultItem (the pure highlight parser; no
// dangerouslySetInnerHTML). The §4 states — idle prompt, loading, results,
// zero-results, `invalid_query` (400 → "adjust your search"), and 503
// `search_unavailable` (non-blocking) — mirror the live read pages' handling.

import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MagnifyingGlass,
  X,
  CaretLeft,
  CaretRight,
  WarningCircle,
} from '@phosphor-icons/react';
import type { SearchHit, SearchFacets } from '@portal/shared-types';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorPanel } from '@/components/ui/error-panel';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/query-skeletons';
import { RepoScopeToggle } from '@/components/search/repo-scope-toggle';
import { SearchResultItem } from '@/components/search/search-result-item';
import { useSearch } from '@/hooks/use-search';
import { ApiError } from '@/lib/api-client';

/** Explicit page size for the `/search` surface (API default is 10; clamp [1,50]). */
const PER_PAGE = 20;

const LISTBOX_ID = 'search-results-listbox';
const optionId = (index: number) => `search-result-option-${index}`;

/**
 * The facet filters this page surfaces, each bound to a URL param and one of the
 * value→count maps the API serves. `key` indexes into `SearchFacets` — chips are
 * built ONLY from what the response actually contains.
 */
const FACET_DEFS: { param: string; label: string; key: keyof SearchFacets }[] = [
  { param: 'type', label: 'Type', key: 'docType' },
  { param: 'status', label: 'Status', key: 'docStatus' },
  { param: 'owner', label: 'Owner', key: 'docOwner' },
];

/** Build the RFC-0010 doc-reader route from a hit's own repo/path (never a raw URL). */
function docHref(repo: string, path: string): string {
  return `/projects/${encodeURIComponent(repo)}/docs/${path}`;
}

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // ── URL is the source of truth ────────────────────────────────────────────
  const q = searchParams.get('q') ?? '';
  const repo = searchParams.get('repo') || undefined; // bare short name; absent = all
  const type = searchParams.get('type') || undefined;
  const status = searchParams.get('status') || undefined;
  const owner = searchParams.get('owner') || undefined;
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

  const scope: 'repo' | 'all' = repo ? 'repo' : 'all';
  const trimmed = q.trim();
  const hasActiveFilters = Boolean(type || status || owner || repo);

  const result = useSearch({ q, repo, type, status, owner, page, perPage: PER_PAGE });

  const hits: SearchHit[] = useMemo(() => result.data?.data ?? [], [result.data]);
  const facets = result.data?.facets;
  const pagination = result.data?.pagination;

  // ── Keyboard model over the results list (Arrow Up/Down + Enter) ───────────
  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => {
    setActiveIndex(0);
  }, [trimmed, type, status, owner, repo, page]);
  const clampedActive = hits.length > 0 ? Math.min(activeIndex, hits.length - 1) : 0;

  // ── Error classification (G13 envelope codes) ─────────────────────────────
  const apiError = result.error instanceof ApiError ? result.error : null;
  const isInvalidQuery = apiError?.code === 'invalid_query' || apiError?.status === 400;
  const isUnavailable = apiError?.code === 'search_unavailable' || apiError?.status === 503;

  // ── §4 display states ─────────────────────────────────────────────────────
  const showIdle = trimmed.length === 0;
  const showLoading = !showIdle && result.isLoading;
  const showResults = !showIdle && !showLoading && !result.isError && hits.length > 0;
  const showNoResults =
    !showIdle && !showLoading && !result.isError && result.data !== undefined && hits.length === 0;

  // ── URL writers ───────────────────────────────────────────────────────────
  // Any non-`page` change resets to page 1 (a new filter starts a new result set).
  function setParam(key: string, value: string | undefined, opts?: { replace?: boolean }) {
    const next = new URLSearchParams(searchParams);
    if (value === undefined || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setSearchParams(next, { replace: opts?.replace });
  }

  // Query text: write on every keystroke (URL stays authoritative + shareable),
  // but `replace` so typing doesn't flood the history stack — useSearch debounces
  // the actual request. Facet/scope/page changes push (back/forward steps them).
  function onQueryChange(value: string) {
    setParam('q', value, { replace: true });
  }

  function toggleFacet(param: string, value: string) {
    setParam(param, searchParams.get(param) === value ? undefined : value);
  }

  function onScopeChange(next: 'repo' | 'all') {
    if (next === 'all') setParam('repo', undefined);
    else if (repo) setParam('repo', repo);
  }

  function clearFilters() {
    // Keep the query; drop every facet + scope filter and reset to page 1.
    const next = new URLSearchParams();
    if (q) next.set('q', q);
    setSearchParams(next);
  }

  function resetToDefaults() {
    setSearchParams(new URLSearchParams());
  }

  function selectHit(hit: SearchHit) {
    navigate(docHref(hit.repo, hit.path));
  }

  function onListKeyDown(event: KeyboardEvent<HTMLUListElement>) {
    if (hits.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, hits.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      const hit = hits[clampedActive];
      if (hit) {
        event.preventDefault();
        selectHit(hit);
      }
    }
  }

  return (
    <>
      <PageHeader
        title="Search"
        description="Full-text search across every document in the portal."
      />

      {/* Query input — always present + interactive (URL-bound). */}
      <div className="relative mb-6">
        <MagnifyingGlass
          size={16}
          aria-hidden="true"
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted"
        />
        <input
          type="search"
          aria-label="Search documents"
          value={q}
          onChange={(e) => onQueryChange(e.target.value)}
          autoFocus
          placeholder="Search docs, runbooks, architecture…"
          className="min-h-[44px] w-full rounded-lg border border-border bg-surface py-2.5 pl-10 pr-3 text-sm text-text placeholder:text-(--c-placeholder) transition-all focus:border-accent focus:outline-none focus:ring-2 focus:ring-[rgba(0,184,238,0.15)]"
        />
      </div>

      {showIdle ? (
        <div className="rounded-xl border border-border bg-surface shadow-(--c-shadow-sm)">
          <EmptyState
            icon={<MagnifyingGlass size={22} />}
            title="Search across every doc"
            description="Start typing to search titles and content from every connected repository, powered by Meilisearch."
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
          {/* ── Filter rail: scope + served-facet chips ──────────────────── */}
          <aside aria-label="Search filters" className="flex flex-col gap-6">
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">
                Scope
              </p>
              <RepoScopeToggle scope={scope} repoName={repo} onChange={onScopeChange} />
            </div>

            {facets &&
              FACET_DEFS.map((def) => {
                const entries = Object.entries(facets[def.key]);
                if (entries.length === 0) return null; // never build chips the API didn't serve
                const selected = searchParams.get(def.param) ?? undefined;
                return (
                  <div key={def.param}>
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">
                      {def.label}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {entries.map(([value, count]) => {
                        const active = selected === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            aria-pressed={active}
                            aria-label={`${def.label}: ${value} (${count})`}
                            onClick={() => toggleFacet(def.param, value)}
                            className={[
                              'inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-medium transition-colors',
                              active
                                ? 'border-accent bg-accent-faint text-text'
                                : 'border-border text-muted hover:text-text',
                            ].join(' ')}
                          >
                            <span className="truncate">{value}</span>
                            <span className="text-[10px] tabular-nums text-muted/70">{count}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex min-h-[44px] items-center gap-1 self-start text-xs font-medium text-muted hover:text-text"
              >
                <X size={13} weight="bold" />
                Clear filters
              </button>
            )}
          </aside>

          {/* ── Results region ───────────────────────────────────────────── */}
          <section aria-label="Search results" aria-busy={result.isFetching} className="min-w-0">
            {/* 503: non-blocking notice — the input + filters above stay usable. */}
            {isUnavailable && (
              <div
                role="status"
                className="mb-4 flex items-start gap-2.5 rounded-lg border border-[rgba(255,176,32,0.25)] bg-warning-bg px-4 py-3 text-sm text-text"
              >
                <WarningCircle size={18} weight="fill" className="mt-0.5 shrink-0 text-warning" />
                <div className="flex-1">
                  <p className="font-medium">Search is temporarily unavailable.</p>
                  <p className="text-muted">
                    The search index isn&rsquo;t responding right now. Please try again in a moment.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  className="min-h-[44px]"
                  onClick={() => void result.refetch()}
                >
                  Try again
                </Button>
              </div>
            )}

            {showLoading ? (
              <SearchResultsSkeleton />
            ) : isInvalidQuery ? (
              <div className="rounded-xl border border-border bg-surface shadow-(--c-shadow-sm)">
                <EmptyState
                  icon={<WarningCircle size={22} />}
                  title="Adjust your search"
                  description="That search couldn’t be run — it may contain unsupported characters or filters. Try simplifying your query or resetting the filters."
                  action={
                    <Button size="md" variant="secondary" className="min-h-[44px]" onClick={resetToDefaults}>
                      <X size={15} weight="bold" />
                      Reset search
                    </Button>
                  }
                />
              </div>
            ) : isUnavailable ? null /* banner above already covers it, non-blocking */ : result.isError ? (
              <ErrorPanel error={result.error} onRetry={() => void result.refetch()} />
            ) : showResults ? (
              <>
                <ul
                  id={LISTBOX_ID}
                  role="listbox"
                  aria-label="Search results"
                  tabIndex={0}
                  aria-activedescendant={optionId(clampedActive)}
                  onKeyDown={onListKeyDown}
                  className="flex flex-col gap-0.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  {hits.map((hit, i) => (
                    <li
                      key={hit.id}
                      id={optionId(i)}
                      role="option"
                      aria-selected={i === clampedActive}
                      onMouseEnter={() => setActiveIndex(i)}
                    >
                      <SearchResultItem
                        result={hit}
                        isActive={i === clampedActive}
                        onSelect={selectHit}
                      />
                    </li>
                  ))}
                </ul>

                {pagination && pagination.totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <p className="text-xs text-muted">
                      Page {pagination.page} of {pagination.totalPages} · {pagination.total}{' '}
                      {pagination.total === 1 ? 'result' : 'results'}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="min-h-[44px]"
                        disabled={pagination.page <= 1}
                        onClick={() => setParam('page', String(pagination.page - 1))}
                      >
                        <CaretLeft size={14} weight="bold" />
                        Previous
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="min-h-[44px]"
                        disabled={pagination.page >= pagination.totalPages}
                        onClick={() => setParam('page', String(pagination.page + 1))}
                      >
                        Next
                        <CaretRight size={14} weight="bold" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : showNoResults ? (
              <div className="rounded-xl border border-border bg-surface shadow-(--c-shadow-sm)">
                <EmptyState
                  icon={<MagnifyingGlass size={22} />}
                  title={`No results for “${trimmed}”`}
                  description={
                    hasActiveFilters
                      ? 'Nothing matched with the current filters. Try clearing filters or switching to All projects to broaden your search.'
                      : 'Nothing matched your search. Try different keywords or check the spelling.'
                  }
                  action={
                    hasActiveFilters ? (
                      <Button size="md" variant="secondary" className="min-h-[44px]" onClick={clearFilters}>
                        <X size={15} weight="bold" />
                        Clear filters
                      </Button>
                    ) : undefined
                  }
                />
              </div>
            ) : null}
          </section>
        </div>
      )}
    </>
  );
}

/** Loading placeholder mirroring the result-row layout (keeps filters/pager stable). */
function SearchResultsSkeleton() {
  return (
    <div role="status" aria-label="Searching" className="flex flex-col gap-1">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-lg px-3 py-2">
          <Skeleton className="mb-2 h-4 w-1/3" />
          <Skeleton className="mb-2 h-3 w-1/4" />
          <Skeleton className="h-3 w-3/4" />
        </div>
      ))}
    </div>
  );
}
